import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  INCOME_RANGE,
  OCCUPATION,
  PEP_STATUS,
  SOURCE_OF_WEALTH,
} from "creso-core/constants";
import { SecondHolderOtherInfoSchema } from "creso-core/zod";
import { AddSecondHolderFormData } from "creso-core/types";
import {
  Button,
  Form,
  FormField,
  FormSelect,
  FormToggleGroup,
  useMultiStepForm,
} from "web-design-system";
import { useSaveSecondHolderProfile } from "@crm/hooks/use-second-holder";

export function OtherInfoStep({
  isFinalStep = false,
}: {
  isFinalStep?: boolean;
}) {
  const { formData, previousStep } =
    useMultiStepForm<AddSecondHolderFormData>();
  const { saveAndContinue, isPending, secondHolder } =
    useSaveSecondHolderProfile();
  const isLinkedToExistingClient =
    !!formData.linked_client_id || !!formData.has_other_client;
  // Existing client's values are read-only; missing ones can still be filled
  const lockIfFilled = (value?: string | null) =>
    isLinkedToExistingClient && !!value;

  const form = useForm<z.infer<typeof SecondHolderOtherInfoSchema>>({
    resolver: zodResolver(SecondHolderOtherInfoSchema),
    values: {
      occupation: formData.occupation ?? secondHolder?.occupation ?? "",
      source_of_wealth:
        formData.source_of_wealth ?? secondHolder?.source_of_wealth ?? "",
      income_slab: formData.income_slab ?? secondHolder?.income_slab ?? "",
      pep_details: formData.pep_details ?? secondHolder?.pep_details ?? "",
    },
  });

  const onSubmit = (data: z.infer<typeof SecondHolderOtherInfoSchema>) => {
    return saveAndContinue(data);
  };

  return (
    <Form {...form}>
      <form
        className="flex min-h-0 flex-1 flex-col gap-6 pt-3"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <span className="text-lg font-semibold">Other Information</span>
        <div className="flex flex-col gap-10">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="occupation"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Occupation"
                  options={OCCUPATION}
                  placeholder="Select"
                  disabled={lockIfFilled(formData.occupation)}
                />
              )}
            />
            <FormField
              control={form.control}
              name="source_of_wealth"
              render={({ field }) => (
                <FormSelect
                  {...field}
                  label="Income Source"
                  options={SOURCE_OF_WEALTH}
                  placeholder="Select"
                  disabled={lockIfFilled(formData.source_of_wealth)}
                />
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="income_slab"
            render={({ field }) => (
              <FormToggleGroup
                {...field}
                label="Income Range"
                options={INCOME_RANGE}
                disabled={lockIfFilled(formData.income_slab)}
              />
            )}
          />
          <FormField
            control={form.control}
            name="pep_details"
            render={({ field }) => (
              <FormToggleGroup
                {...field}
                label="Political exposure"
                options={PEP_STATUS}
                disabled={lockIfFilled(formData.pep_details)}
              />
            )}
          />
        </div>
        <div className="mt-auto flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={previousStep}>
            Previous
          </Button>
          <Button type="submit" disabled={isPending}>
            {isFinalStep ? "Update" : "Next"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
